import { getRemainingVisualViewportHeight } from '@src/packages/utils';

const SCROLLPORT_OVERFLOW = ['auto', 'scroll', 'overlay'];

const toPx = (value: string): number => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Height the pane can take without overflowing the surface that scrolls it.
 *
 * Measuring straight to the visual viewport bottom ignores the chrome ancestors
 * reserve below the pane (`BoxContainer` alone adds bottom padding plus margin),
 * so the pane ends up taller than its scroll parent and the page grows a second
 * scrollbar next to the pane's own one.
 *
 * The walk stops at the first scrollport: that element's bottom edge is the real
 * limit, and anything above it scrolls as a whole.
 *
 * Pass `constrainToScrollParent: false` when that scrollport's height depends on
 * the pane itself. Measuring to it would shrink the pane to whatever is already
 * painted, so the limit is the visual viewport instead.
 */
const getAvailablePaneHeight = (
  element: HTMLElement,
  viewportHeight: number,
  constrainToScrollParent = true,
): number => {
  let bottomLimit = viewportHeight;
  let reserved = toPx(window.getComputedStyle(element).marginBottom);

  if (!constrainToScrollParent) {
    const available = getRemainingVisualViewportHeight(
      element.getBoundingClientRect().top,
      viewportHeight,
    );

    return Math.max(0, Math.round(available - reserved));
  }

  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { paddingBottom, borderBottomWidth, marginBottom, overflowY } =
      window.getComputedStyle(parent);

    reserved += toPx(paddingBottom) + toPx(borderBottomWidth);

    if (SCROLLPORT_OVERFLOW.includes(overflowY)) {
      bottomLimit = Math.min(bottomLimit, parent.getBoundingClientRect().bottom);
      break;
    }

    reserved += toPx(marginBottom);
  }

  const available = getRemainingVisualViewportHeight(
    element.getBoundingClientRect().top,
    bottomLimit,
  );

  return Math.max(0, Math.round(available - reserved));
};

export default getAvailablePaneHeight;
